import type { Role } from "@template/types";

// Re-exported so callers can `import type { Role } from "./permissions"` —
// the canonical definition lives in @template/types since it is also part of
// the User contract returned by the backend (auth/me, users list, etc).
export type { Role };

export interface PermissionCatalogEntry {
  /** Stable permission key, e.g. "budget.edit". */
  k: string;
  /** Human-readable label shown in the Roles & Permissions matrix. */
  l: string;
  /** Group heading the permission is displayed under. */
  g: string;
}

/**
 * Config-driven RBAC + PBAC permission catalog — ported verbatim from the
 * source governance-portal prototype. Adding a new permission or role never
 * requires touching component code, only this file.
 */
export const PERM_CATALOG: PermissionCatalogEntry[] = [
  { k: "dashboard.view", l: "Dashboard — View", g: "Planning" },

  { k: "budget.view", l: "Budget — View", g: "Budget" },
  { k: "budget.edit", l: "Budget — Add / Edit Draft", g: "Budget" },
  { k: "budget.editFrozen", l: "Budget — Edit Frozen (revision)", g: "Budget" },
  { k: "budget.submit", l: "Budget — Submit for Approval", g: "Budget" },
  { k: "budget.delete", l: "Budget — Delete (soft)", g: "Budget" },
  { k: "budget.export", l: "Budget — Export", g: "Budget" },

  { k: "revenue.view", l: "Revenue — View", g: "Revenue" },
  { k: "revenue.edit", l: "Revenue — Add / Edit", g: "Revenue" },
  { k: "revenue.approve", l: "Revenue — Approve", g: "Revenue" },

  { k: "reports.view", l: "Reports — View", g: "Reports" },
  { k: "reports.export", l: "Reports — Export (PDF/Excel/CSV)", g: "Reports" },

  { k: "tally.upload", l: "Tally — Upload", g: "Tally" },
  { k: "tally.validate", l: "Tally — Validate", g: "Tally" },

  { k: "approval.act", l: "Approvals — Approve / Reject", g: "Approvals" },
  { k: "approval.publish", l: "Approvals — Publish to Production", g: "Approvals" },

  { k: "freeze.month", l: "Freeze — Month", g: "Period Control" },
  { k: "freeze.quarter", l: "Freeze — Quarter", g: "Period Control" },
  { k: "freeze.half", l: "Freeze — Half-Year", g: "Period Control" },
  { k: "freeze.year", l: "Freeze — Year", g: "Period Control" },
  { k: "period.unlock", l: "Period — Reopen / Unlock", g: "Period Control" },

  { k: "version.view", l: "Versions — View / Compare", g: "Versions" },
  { k: "version.rollback", l: "Versions — Rollback", g: "Versions" },

  { k: "audit.view", l: "Audit Trail — View", g: "Governance" },

  { k: "users.view", l: "Users — View", g: "Administration" },
  { k: "users.manage", l: "Users — Manage (all)", g: "Administration" },
  { k: "users.manageFinanceTeam", l: "Users — Manage Finance Team", g: "Administration" },
  { k: "roles.manage", l: "Roles & Permissions — Manage", g: "Administration" },
  { k: "settings.manage", l: "System Settings — Manage", g: "Administration" },
];

export const ALL_PERMS: string[] = PERM_CATALOG.map((p) => p.k);

export const ROLES: Role[] = ["CEO", "Director", "Finance Head", "Finance Team", "Viewer"];

export type RolePermissions = Record<string, string[]>;

/**
 * Default permission grants per role — ported verbatim from the source
 * prototype's `DEFAULT_ROLE_PERMS`. CEO implicitly has every permission
 * (see `can()` below) regardless of what's listed here.
 */
export const DEFAULT_ROLE_PERMS: RolePermissions = {
  CEO: ALL_PERMS.slice(),
  Director: [
    "dashboard.view",
    "budget.view",
    "budget.edit",
    "budget.editFrozen",
    "budget.submit",
    "budget.delete",
    "budget.export",
    "revenue.view",
    "revenue.edit",
    "revenue.approve",
    "reports.view",
    "reports.export",
    "tally.upload",
    "tally.validate",
    "approval.act",
    "approval.publish",
    "freeze.month",
    "freeze.quarter",
    "freeze.half",
    "freeze.year",
    "period.unlock",
    "version.view",
    "version.rollback",
    "audit.view",
    "users.view",
    "users.manage",
    "settings.manage",
  ],
  "Finance Head": [
    "dashboard.view",
    "budget.view",
    "budget.edit",
    "budget.editFrozen",
    "budget.submit",
    "budget.delete",
    "budget.export",
    "revenue.view",
    "revenue.edit",
    "reports.view",
    "reports.export",
    "tally.upload",
    "tally.validate",
    "approval.act",
    "approval.publish",
    "freeze.month",
    "freeze.quarter",
    "freeze.half",
    "freeze.year",
    "version.view",
    "audit.view",
    "users.view",
    "users.manageFinanceTeam",
  ],
  "Finance Team": [
    "dashboard.view",
    "budget.view",
    "budget.edit",
    "budget.submit",
    "budget.export",
    "revenue.view",
    "reports.view",
    "reports.export",
    "version.view",
  ],
  Viewer: [
    "dashboard.view",
    "budget.view",
    "revenue.view",
    "reports.view",
    "reports.export",
    "version.view",
  ],
};

/** CEO always has every permission; everyone else is looked up in `rolePerms`. */
export function hasPerm(rolePerms: RolePermissions, role: Role | string, permKey: string): boolean {
  if (role === "CEO") return true;
  return (rolePerms[role] ?? []).indexOf(permKey) >= 0;
}

/** Convenience helper against the (possibly admin-edited) default grants. */
export function can(
  role: Role | string,
  permKey: string,
  rolePerms: RolePermissions = DEFAULT_ROLE_PERMS,
): boolean {
  return hasPerm(rolePerms, role, permKey);
}
